import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../../firebase/config';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
// import './Steps.css';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';
import { runStorageDiagnostic } from '../../utils/storageDiagnostic';
import StorageErrorModal from '../StorageErrorModal';

interface Certification {
  id: string;
  name_en: string;
  name_es: string;
  issuer_en: string;
  issuer_es: string;
  description_en: string;
  description_es: string;
  duration_en: string;
  duration_es: string;
  categoryId: string;
}

interface UserCertification {
  id: string;
  certificationId: string;
  date: string;
  file: File | null;
  preview: string | null;
  frontImage: File | null;
  frontPreview: string | null;
  backImage: File | null;
  backPreview: string | null;
  fileUrl?: string | null;
  frontImageUrl?: string | null;
  backImageUrl?: string | null;
}

interface Step5CertificationsProps {
  onComplete: (step: string) => void;
  initialCertifications?: UserCertification[];
  isEditMode?: boolean;
  existingCertifications?: any[];
}

const Step5Certifications: React.FC<Step5CertificationsProps> = ({ onComplete, initialCertifications, isEditMode = false, existingCertifications = [] }) => {
  const { t } = useTranslation();
  const [availableCertifications, setAvailableCertifications] = useState<Certification[]>([]);
  const [userCertifications, setUserCertifications] = useState<UserCertification[]>(initialCertifications || []);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [userLanguage, setUserLanguage] = useState<string>('en');
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState<any>(null);

  useEffect(() => {
    if (initialCertifications && initialCertifications.length > 0) {
      setUserCertifications(initialCertifications);
    } else if (!isEditMode) {
      // Si no estamos en modo edición y no hay certificaciones iniciales, crear una vacía para agregar
      setUserCertifications([{
        id: Date.now().toString(),
        certificationId: '',
        date: '',
        file: null,
        preview: null,
        frontImage: null,
        frontPreview: null,
        backImage: null,
        backPreview: null
      }]);
    }
  }, [initialCertifications, isEditMode]);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!auth.currentUser) {
          logger.error('Step5Certifications', 'No hay usuario autenticado');
          setError('No hay usuario autenticado. Por favor, inicia sesión.');
          return;
        }

        // Verificar que el usuario tenga un token válido
        try {
          const token = await auth.currentUser.getIdToken(true);
          if (!token) {
            logger.error('Step5Certifications', 'Token de autenticación inválido');
            setError('Token de autenticación inválido. Por favor, inicia sesión nuevamente.');
            return;
          }
          logger.info('Step5Certifications', 'Token de autenticación válido obtenido');
        } catch (tokenError) {
          logger.error('Step5Certifications', 'Error al obtener token:', tokenError);
          setError('Error de autenticación. Por favor, inicia sesión nuevamente.');
          return;
        }

        // Cargar el idioma y sector del usuario
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        let userLanguage = 'en';
        let sector = 'construction';
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userLanguage = userData.language || 'en';
          if (userData.sector === 'health') {
            sector = 'health';
          }
        }
        setUserLanguage(userLanguage);

        // Cargar las certificaciones disponibles según el sector
        const certsDocId = sector === 'health' ? 'Health_skills_certs' : 'skills_and_certs';
        const certsDoc = await getDoc(doc(db, 'Skills_and_certifications', certsDocId));
        if (certsDoc.exists()) {
          const data = certsDoc.data();
          if (data.certifications) {
            // Soportar array o mapa
            const certsArr = Array.isArray(data.certifications) ? data.certifications : Object.values(data.certifications || {});
            setAvailableCertifications(certsArr);
            logger.info('Step5Certifications', 'Certificaciones disponibles cargadas:', certsArr.length);
          }
        }
      } catch (error) {
        logger.error('Step5Certifications', 'Error al cargar datos', error);
        setError(t('onboarding.certifications.errorLoading'));
      }
    };

    loadData();
  }, [t]);

  const handleAddCertification = () => {
    setUserCertifications([
      ...userCertifications,
      {
        id: Date.now().toString(),
        certificationId: '',
        date: '',
        file: null,
        preview: null,
        frontImage: null,
        frontPreview: null,
        backImage: null,
        backPreview: null
      }
    ]);
  };

  const handleRemoveCertification = (id: string) => {
    setUserCertifications(userCertifications.filter(cert => cert.id !== id));
  };

  const handleCertificationChange = (id: string, field: keyof UserCertification, value: string) => {
    setUserCertifications(userCertifications.map(cert => 
      cert.id === id ? { ...cert, [field]: value } : cert
    ));
  };

  const handleFileChange = async (id: string, file: File) => {
    if (file.type !== 'application/pdf') {
      setError(t('onboarding.certifications.errorFileType'));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setUserCertifications(userCertifications.map(cert => 
        cert.id === id ? { 
          ...cert, 
          file,
          preview: reader.result as string 
        } : cert
      ));
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = async (id: string, file: File, side: 'front' | 'back') => {
    if (!file.type.startsWith('image/')) {
      setError(t('onboarding.certifications.errorImageType'));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setUserCertifications(userCertifications.map(cert => {
        if (cert.id !== id) return cert;
        if (side === 'front') {
          return { ...cert, frontImage: file, frontPreview: reader.result as string };
        } else {
          return { ...cert, backImage: file, backPreview: reader.result as string };
        }
      }));
    };
    reader.readAsDataURL(file);
  };

  // Función para verificar permisos de Storage usando el diagnóstico
  const checkStoragePermissions = async (): Promise<boolean> => {
    try {
      const diagnosticResult = await runStorageDiagnostic();
      if (diagnosticResult.success) {
        logger.info('Step5Certifications', 'Diagnóstico de Storage exitoso:', diagnosticResult.details);
        return true;
      } else {
        logger.error('Step5Certifications', 'Diagnóstico de Storage falló:', diagnosticResult.error);
        setError(`Error de permisos: ${diagnosticResult.error}`);
        return false;
      }
    } catch (error: any) {
      logger.error('Step5Certifications', 'Error al ejecutar diagnóstico de Storage:', error);
      setError('Error al verificar permisos de Storage');
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Verificar autenticación
      if (!auth.currentUser) {
        throw new Error('Usuario no autenticado');
      }

      // Verificar que el usuario tenga un token válido
      const token = await auth.currentUser.getIdToken(true);
      if (!token) {
        throw new Error('Token de autenticación inválido');
      }

      const userId = auth.currentUser.uid;
      
      // Verificar permisos de Storage antes de continuar
      const hasStoragePermissions = await checkStoragePermissions();
      if (!hasStoragePermissions) {
        throw new Error('No tienes permisos para subir archivos. Contacta al administrador.');
      }
      const timestamp = Date.now();

      logger.info('Step5Certifications', 'Iniciando subida de certificaciones para usuario:', userId);

      // Validar que cada certificación tenga al menos un archivo (PDF o imagen frontal o posterior)
      for (const cert of userCertifications) {
        if (!cert.file && !cert.frontImage && !cert.backImage) {
          setError(t('onboarding.certifications.errorRequiredFileOrImage'));
          setLoading(false);
          return;
        }
      }

      // Subir cada certificación
      const uploadedCertifications = await Promise.all(
        userCertifications.map(async (cert) => {
          let fileUrl = null;
          let frontImageUrl = null;
          let backImageUrl = null;

          try {
            if (cert.file) {
              // Sanitizar el nombre del archivo para evitar problemas con caracteres especiales
              const sanitizedFileName = cert.file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
              const fileRef = ref(storage, `users/${userId}/certifications/${timestamp}_${sanitizedFileName}`);
              
              logger.info('Step5Certifications', 'Subiendo archivo PDF:', sanitizedFileName);
              await uploadBytes(fileRef, cert.file);
              fileUrl = await getDownloadURL(fileRef);
              logger.info('Step5Certifications', 'Archivo PDF subido exitosamente:', fileUrl);
            }
            
            if (cert.frontImage) {
              const sanitizedFrontName = cert.frontImage.name.replace(/[^a-zA-Z0-9.-]/g, '_');
              const frontRef = ref(storage, `users/${userId}/certifications/${timestamp}_front_${sanitizedFrontName}`);
              
              logger.info('Step5Certifications', 'Subiendo imagen frontal:', sanitizedFrontName);
              await uploadBytes(frontRef, cert.frontImage);
              frontImageUrl = await getDownloadURL(frontRef);
              logger.info('Step5Certifications', 'Imagen frontal subida exitosamente:', frontImageUrl);
            }
            
            if (cert.backImage) {
              const sanitizedBackName = cert.backImage.name.replace(/[^a-zA-Z0-9.-]/g, '_');
              const backRef = ref(storage, `users/${userId}/certifications/${timestamp}_back_${sanitizedBackName}`);
              
              logger.info('Step5Certifications', 'Subiendo imagen posterior:', sanitizedBackName);
              await uploadBytes(backRef, cert.backImage);
              backImageUrl = await getDownloadURL(backRef);
              logger.info('Step5Certifications', 'Imagen posterior subida exitosamente:', backImageUrl);
            }
          } catch (uploadError: any) {
            logger.error('Step5Certifications', 'Error al subir archivo:', uploadError);
            throw new Error(`Error al subir archivo: ${uploadError.message}`);
          }

          const certification = availableCertifications.find(c => c.id === cert.certificationId);
          if (!certification) {
            logger.warn('Step5Certifications', 'Certificación no encontrada:', cert.certificationId);
            return null;
          }

          return {
            certificationId: cert.certificationId,
            // Campos en ambos idiomas
            name_en: certification.name_en,
            name_es: certification.name_es,
            issuer_en: certification.issuer_en,
            issuer_es: certification.issuer_es,
            description_en: certification.description_en,
            description_es: certification.description_es,
            duration_en: certification.duration_en,
            duration_es: certification.duration_es,
            // Campos para compatibilidad (usar el idioma actual)
            name: userLanguage === 'es' ? certification.name_es : certification.name_en,
            issuer: userLanguage === 'es' ? certification.issuer_es : certification.issuer_en,
            date: cert.date,
            fileUrl,
            frontImageUrl,
            backImageUrl,
            status: 'pending', // Estado inicial para aprobación
            uploadedAt: new Date().toISOString()
          };
        })
      );

      // Filtrar certificaciones nulas y actualizar el documento
      const validCertifications = uploadedCertifications.filter(cert => cert !== null);
      
      logger.info('Step5Certifications', 'Actualizando documento de usuario con certificaciones:', validCertifications.length);
      
      // Si no estamos en modo edición, agregar a las certificaciones existentes
      let finalCertifications = validCertifications;
      if (!isEditMode && existingCertifications) {
        finalCertifications = [...existingCertifications, ...validCertifications];
        logger.info('Step5Certifications', 'Agregando nuevas certificaciones a las existentes. Total:', finalCertifications.length);
      }
      
      await updateDoc(doc(db, 'users', userId), {
        certifications: finalCertifications,
        updatedAt: new Date().toISOString()
      });

      logger.info('Step5Certifications', 'Certificaciones guardadas exitosamente');
      onComplete('reload');
          } catch (error: any) {
        logger.error('Step5Certifications', 'Error al guardar certificaciones', error);
        
        // Mensajes de error más específicos
        let errorMessage = t('onboarding.certifications.errorSaving');
        if (error.message.includes('permission-denied')) {
          errorMessage = 'Error de permisos: No tienes autorización para subir archivos.';
        } else if (error.message.includes('unauthenticated')) {
          errorMessage = 'Error de autenticación: Por favor, inicia sesión nuevamente.';
        } else if (error.message.includes('storage/unauthorized')) {
          errorMessage = 'Error de autorización en Storage: Verifica tus permisos.';
        } else if (error.message.includes('storage/quota-exceeded')) {
          errorMessage = 'Error: Se ha excedido la cuota de almacenamiento.';
        } else if (error.message.includes('storage/retry-limit-exceeded')) {
          errorMessage = 'Error: Se excedió el límite de reintentos. Intenta nuevamente.';
        } else if (error.message.includes('storage/invalid-checksum')) {
          errorMessage = 'Error: El archivo está corrupto. Intenta subir el archivo nuevamente.';
        } else if (error.message.includes('storage/canceled')) {
          errorMessage = 'Error: La operación fue cancelada.';
        } else if (error.message.includes('storage/invalid-event-name')) {
          errorMessage = 'Error interno del sistema. Contacta soporte.';
        } else if (error.message.includes('storage/invalid-url')) {
          errorMessage = 'Error: URL inválida generada.';
        } else if (error.message.includes('storage/invalid-argument')) {
          errorMessage = 'Error: Argumento inválido en la operación.';
        } else if (error.message.includes('storage/no-default-bucket')) {
          errorMessage = 'Error: No se encontró el bucket de almacenamiento.';
        } else if (error.message.includes('storage/cannot-slice-blob')) {
          errorMessage = 'Error: No se puede procesar el archivo. Intenta con otro archivo.';
        } else if (error.message.includes('storage/server-file-wrong-size')) {
          errorMessage = 'Error: El tamaño del archivo no coincide. Intenta nuevamente.';
        }
        
        setError(errorMessage);
        setErrorDetails(error);
        setShowErrorModal(true);
      } finally {
        setLoading(false);
      }
  };

  // Detectar si es modal de edición/creación individual
  const isModal = !!initialCertifications && initialCertifications.length === 1;

  return (
    <div className="w-full max-w-3xl mx-auto p-8 bg-white rounded-[20px_40px_20px_40px] shadow-2xl border border-white/40 animate-fadeIn font-['Montserrat']">
      <h2 className="text-3xl font-bold text-red-600 mb-4 text-center">
        {t('onboarding.certifications.title')}
      </h2>
      <p className="text-gray-600 text-center mb-8 text-lg">
        {t('onboarding.certifications.description')}
      </p>
      <form onSubmit={handleSubmit} className="space-y-6">
        {userCertifications.map((cert) => (
          <div key={cert.id} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 shadow-lg">
            <p className="text-gray-700 text-sm mb-4">
              {t('onboarding.certifications.uploadInfo')}
            </p>
            <div className="space-y-4">
              <div>
                <label htmlFor={`certification-${cert.id}`} className="block text-sm font-medium text-gray-900 mb-2">
                  {t('onboarding.certifications.name')}
                </label>
                <select
                  id={`certification-${cert.id}`}
                  value={cert.certificationId}
                  onChange={(e) => handleCertificationChange(cert.id, 'certificationId', e.target.value)}
                  required
                  aria-label={t('onboarding.certifications.name')}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">{t('onboarding.certifications.selectCertification')}</option>
                  {availableCertifications.map((availableCert) => (
                    <option key={availableCert.id} value={availableCert.id} className="bg-gray-800 text-white">
                      {userLanguage === 'es' ? availableCert.name_es : availableCert.name_en}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={`date-${cert.id}`} className="block text-sm font-medium text-gray-900 mb-2">
                  {t('onboarding.certifications.date')}
                </label>
                <input
                  id={`date-${cert.id}`}
                  type="date"
                  value={cert.date}
                  onChange={(e) => handleCertificationChange(cert.id, 'date', e.target.value)}
                  required
                  aria-label={t('onboarding.certifications.date')}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>
              <div>
                <label htmlFor={`file-${cert.id}`} className="block text-sm font-medium text-gray-900 mb-2">
                  {t('onboarding.certifications.file')}
                </label>
                <div className="relative">
                  <input
                    id={`file-${cert.id}`}
                    type="file"
                    accept=".pdf"
                    onChange={(e) => e.target.files && handleFileChange(cert.id, e.target.files[0])}
                    aria-label={t('onboarding.certifications.file')}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 file:cursor-pointer"
                  />
                  {cert.preview && (
                    <div className="mt-2 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                      <span className="text-green-300 text-sm font-medium">
                        {t('onboarding.certifications.fileSelected')}
                      </span>
                    </div>
                  )}
                  {/* PDF existente */}
                  {cert.fileUrl && !cert.file && (
                    <div className="mt-2 flex items-center gap-2">
                      <a href={cert.fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline" download>
                        PDF actual
                      </a>
                      <button type="button" onClick={() => setUserCertifications(userCertifications.map(c => c.id === cert.id ? { ...c, fileUrl: null } : c))} className="text-red-500 hover:text-red-700 text-sm font-semibold">{t('common.delete')}</button>
                    </div>
                  )}
                  {/* Si no hay archivo cargado ni existente */}
                  {!cert.file && !cert.fileUrl && (
                    <div className="mt-2 text-gray-400 text-xs">{t('onboarding.certifications.noFileSelected') || 'Sin archivos seleccionados'}</div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  {t('onboarding.certifications.frontImage') || 'Imagen Frontal'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => e.target.files && handleImageChange(cert.id, e.target.files[0], 'front')}
                  aria-label={t('onboarding.certifications.frontImage')}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 file:cursor-pointer"
                />
                {cert.frontPreview && (
                  <div className="mt-2 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                    <img src={cert.frontPreview} alt="Frontal" className="max-h-24 rounded" />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  {t('onboarding.certifications.backImage') || 'Imagen Posterior'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => e.target.files && handleImageChange(cert.id, e.target.files[0], 'back')}
                  aria-label={t('onboarding.certifications.backImage')}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 file:cursor-pointer"
                />
                {cert.backPreview && (
                  <div className="mt-2 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                    <img src={cert.backPreview} alt="Posterior" className="max-h-24 rounded" />
                  </div>
                )}
              </div>
              {/* Imagen frontal existente */}
              {cert.frontImageUrl && !cert.frontImage && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={cert.frontImageUrl} alt="Frontal" className="max-h-16 rounded border" />
                  <button type="button" onClick={() => setUserCertifications(userCertifications.map(c => c.id === cert.id ? { ...c, frontImageUrl: null } : c))} className="text-red-500 hover:text-red-700 text-sm font-semibold">{t('common.delete')}</button>
                </div>
              )}
              {/* Si no hay imagen frontal cargada ni existente */}
              {!cert.frontImage && !cert.frontImageUrl && (
                <div className="mt-2 text-gray-400 text-xs">{t('onboarding.certifications.noFileSelected') || 'Sin archivos seleccionados'}</div>
              )}
              {/* Imagen posterior existente */}
              {cert.backImageUrl && !cert.backImage && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={cert.backImageUrl} alt="Posterior" className="max-h-16 rounded border" />
                  <button type="button" onClick={() => setUserCertifications(userCertifications.map(c => c.id === cert.id ? { ...c, backImageUrl: null } : c))} className="text-red-500 hover:text-red-700 text-sm font-semibold">{t('common.delete')}</button>
                </div>
              )}
              {/* Si no hay imagen posterior cargada ni existente */}
              {!cert.backImage && !cert.backImageUrl && (
                <div className="mt-2 text-gray-400 text-xs">{t('onboarding.certifications.noFileSelected') || 'Sin archivos seleccionados'}</div>
              )}
              <button
                type="button"
                onClick={() => handleRemoveCertification(cert.id)}
                aria-label={t('onboarding.certifications.remove')}
                className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-200 font-medium"
                style={{ display: isModal ? 'none' : undefined }}
              >
                {t('onboarding.certifications.remove')}
              </button>
            </div>
          </div>
        ))}

        {/* Botón de añadir solo si no es modal */}
        {!isModal && (
          <button
            type="button"
            onClick={handleAddCertification}
            aria-label={t('onboarding.certifications.add')}
            className="w-full px-6 py-4 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-all duration-200 font-medium text-lg"
          >
            {t('onboarding.certifications.add')}
          </button>
        )}

        {error && (
          <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg" role="alert">
            <p className="text-red-300 text-sm mb-3">{error}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setError('')}
                className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded transition-colors duration-200"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={() => {
                  setError('');
                  setLoading(false);
                }}
                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded transition-colors duration-200"
              >
                Reintentar
              </button>
            </div>
          </div>
        )}
        {/* Botones de guardar y cancelar en el modal */}
        {isModal && (
          <div className="flex gap-4 justify-end">
            <button
              type="button"
              onClick={() => onComplete('cancel')}
              className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-xl font-semibold transition-colors duration-200 shadow"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || userCertifications.length === 0}
              aria-label={loading ? t('common.saving') : t('common.saveAndContinue')}
              className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none disabled:scale-100"
            >
              {loading ? t('common.saving') : t('common.save')}
            </button>
          </div>
        )}
        {/* Botón de guardar y continuar para onboarding normal */}
        {!isModal && (
          <button
            type="submit"
            disabled={loading || userCertifications.length === 0}
            aria-label={loading ? t('common.saving') : t('common.saveAndContinue')}
            className="w-full px-6 py-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none disabled:scale-100"
          >
            {loading ? t('common.saving') : t('common.saveAndContinue')}
          </button>
        )}
      </form>

      {/* Modal de error de Storage */}
      <StorageErrorModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setError('');
          setErrorDetails(null);
        }}
        error={error}
        details={errorDetails}
        onRetry={() => {
          setShowErrorModal(false);
          setError('');
          setErrorDetails(null);
          setLoading(false);
        }}
      />
    </div>
  );
};

export default Step5Certifications; 